"use client";

import { Text, Flex, Box, TextField, Link } from "@radix-ui/themes";
import React, { useEffect, useState } from "react";
import { isValidEmail, isValidPassword } from "../services/validationService";
import PasswordViewIcon from "../common/passwordViewIcon";
import AlertMessage from "../common/alertMessage";
import { useRouter } from "next/navigation";

export type EmailCredential = {
  email: string;
  password: string;
  isValidEmail: boolean;
  isValidPassword: boolean;
};

type EmailLoginProps = {
  enablePassword: boolean;
  isSubmitted: boolean;
  credentialData: EmailCredential | null;
  onEnablePhoneNumberView: () => void;
  onEnableForgotPasswordView: () => void;
  handleCredentialData: any;
  loginOptions?: string[] | null;
};

const EmailLogin: React.FC<EmailLoginProps> = (props) => {
  const [enablePasswordView, setPassWordView] = useState<boolean>(false);
  const [viewPassword, setPasswordViewStatus] = useState<boolean>(false);
  const router = useRouter();

  useEffect(() => {
    setPassWordView(props.enablePassword);
  }, [props.enablePassword]);

  return (
    <>
      {!enablePasswordView && (
        <label>
          <Text className="label-name">Email Address</Text>

          <Box>
            <TextField.Root
              className={`${
                !props.credentialData?.isValidEmail && props.isSubmitted
                  ? "input-invalid"
                  : "input-valid"
              } placeholder:text-sm`}
              autoFocus
              size="3"
              maxLength={50}
              placeholder="Enter your email address"
              onChange={(e) =>
                props.handleCredentialData({
                  ...props.credentialData,
                  email: e.target.value,
                  isValidEmail: isValidEmail(e.target.value),
                })
              }
              value={props.credentialData?.email || ""}
              aria-label="Email address"
              role="textbox"
            />
            {!props.credentialData?.isValidEmail && props.isSubmitted && (
              <AlertMessage message="Please enter valid email address" />
            )}
          </Box>

          <Flex
            justify={props.loginOptions?.includes("phone") ? "between" : "end"}
            mt="2"
          >
            {props.loginOptions?.includes("phone") && (
              <Link
                size={{ initial: "1", xl: "2" }}
                underline="hover"
                onClick={() => props.onEnablePhoneNumberView()}
                aria-label="Switch to phone number login"
                role="link"
              >
                <Flex className="items-center" direction={"row"} gap="1">
                  <Text className="font-semibold text-sm text-primary-default">
                    {" "}
                    Use Phone Number
                  </Text>
                </Flex>
              </Link>
            )}

            <Link
              size={{ initial: "1", xl: "2" }}
              underline="hover"
              onClick={() => router.push("forgot-password")}
              aria-label="Go to forgot password page"
              role="link"
            >
              <Text className="font-semibold text-sm text-primary-default">
                {" "}
                Forgot Password?
              </Text>
            </Link>
          </Flex>
        </label>
      )}

      {enablePasswordView && (
        <label>
          <Text className="label-name">Password</Text>

          <Box>
            <TextField.Root
              autoFocus
              size="3"
              maxLength={50}
              type={viewPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={props.credentialData?.password || ""}
              onChange={(e) =>
                props.handleCredentialData({
                  ...props.credentialData,
                  password: e.target.value,
                  isValidPassword: isValidPassword(e.target.value),
                })
              }
              className={`relative ${
                !props.credentialData?.isValidPassword && props.isSubmitted
                  ? "input-invalid"
                  : "input-valid"
              } placeholder:text-sm hide-ms-reveal `}
              aria-label="Password"
              role="textbox"
            >
              <TextField.Slot
                side="right"
                className="absolute right-1 p-1 top-1/2 -translate-y-1/2 cursor-pointer"
              >
                <PasswordViewIcon
                  iconName={viewPassword ? "eyeOpen" : "eyeClose"}
                  handleAction={() => setPasswordViewStatus(!viewPassword)}
                />
              </TextField.Slot>
            </TextField.Root>

            {!props.credentialData?.isValidPassword && props.isSubmitted && (
              <AlertMessage message="Please enter valid password" />
            )}
          </Box>
        </label>
      )}
    </>
  );
};

export default EmailLogin;
